import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, Plus, FileText, Save, Users, UserCheck, Star, Clock, ArrowLeft, LayoutGrid, Award, BookOpen, Coins, Trash2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const PROFICIENCY_LEVELS = [
  { key: "Learning", color: "bg-gray-500", activeColor: "bg-gray-400", textColor: "text-white", short: "L" },
  { key: "Developing", color: "bg-sky-600", activeColor: "bg-sky-400", textColor: "text-white", short: "D" },
  { key: "Proficient", color: "bg-green-600", activeColor: "bg-green-500", textColor: "text-white", short: "P" },
  { key: "Refined", color: "bg-emerald-600", activeColor: "bg-emerald-400", textColor: "text-white", short: "R" },
  { key: "Functional", color: "bg-teal-600", activeColor: "bg-teal-400", textColor: "text-white", short: "F" },
  { key: "Embodied", color: "bg-[#D4AF37]", activeColor: "bg-[#D4AF37]", textColor: "text-black font-bold", short: "E" },
];

const PROFICIENCY_CYCLE = ["Learning", "Developing", "Proficient", "Refined", "Functional", "Embodied"];

const LEGACY_MAP = {
  "Introduced": "Learning", "Foundation": "Learning", "Complete": "Developing",
  "Good": "Proficient", "Novice": "Learning", "Practicing": "Developing",
  "Mastered": "Embodied", "Instructor": "Embodied", "Instructor Level": "Embodied",
  "Intermediate": "Developing", "Advanced": "Refined", "Refining": "Refined",
};

const norm = (p) => {
  if (!p) return "Learning";
  if (PROFICIENCY_CYCLE.includes(p)) return p;
  return LEGACY_MAP[p] || "Learning";
};

const FORM_PILLARS = [
  { key: "Pillar 1", label: "P1", full: "Chou Kup", color: "bg-gray-500" },
  { key: "Pillar 2", label: "P2", full: "Jung Kup", color: "bg-blue-600" },
  { key: "Pillar 3", label: "P3", full: "Gou Kup", color: "bg-orange-500" },
  { key: "Pillar 4", label: "P4", full: "Gau Lin", color: "bg-purple-600" },
];

const SASH_LEVELS = [
  { key: "White", label: "White", color: "bg-gray-300" },
  { key: "Yellow", label: "Yellow", color: "bg-yellow-400" },
  { key: "Orange", label: "Orange", color: "bg-orange-400" },
  { key: "Green", label: "Green", color: "bg-green-500" },
  { key: "Blue", label: "Blue", color: "bg-blue-500" },
  { key: "Purple", label: "Purple", color: "bg-purple-500" },
  { key: "Brown", label: "Brown", color: "bg-amber-700" },
  { key: "Red", label: "Red", color: "bg-red-600" },
  { key: "Black", label: "Black", color: "bg-gray-900" },
];

const parseFormBelts = (form) => {
  const kb = form?.kids_belt;
  if (!kb || kb === "none") return [];
  return kb.split(",").map(b => b.trim()).filter(Boolean);
};

const LEGACY_PILLAR_MAP = {
  "Foundation": "Pillar 1", "Introduced": "Pillar 1",
  "Intermediate": "Pillar 2", "Advanced": "Pillar 3", "Instructor Level": "Pillar 4",
};

const getFormPillar = (form) => {
  if (form?.pillar && (form.pillar.startsWith("Pillar") || form.pillar === "Supplementary")) return form.pillar;
  return LEGACY_PILLAR_MAP[form?.library_proficiency] || "Pillar 1";
};

const BELT_ORDER = [
  { key: "White", label: "White", chinese: "白色腰帶", bg: "bg-gray-200", text: "text-gray-800", dot: "bg-gray-200 border border-gray-400" },
  { key: "Yellow", label: "Yellow", chinese: "黃色腰帶", bg: "bg-yellow-400", text: "text-yellow-900", dot: "bg-yellow-400" },
  { key: "Orange", label: "Orange", chinese: "橙色腰帶", bg: "bg-orange-400", text: "text-orange-900", dot: "bg-orange-400" },
  { key: "Green", label: "Green", chinese: "綠色腰帶", bg: "bg-green-500", text: "text-white", dot: "bg-green-500" },
  { key: "Blue", label: "Blue", chinese: "藍色腰帶", bg: "bg-blue-500", text: "text-white", dot: "bg-blue-500" },
  { key: "Purple", label: "Purple", chinese: "紫色腰帶", bg: "bg-purple-500", text: "text-white", dot: "bg-purple-500" },
  { key: "Brown", label: "Brown", chinese: "棕色腰帶", bg: "bg-amber-700", text: "text-white", dot: "bg-amber-700" },
  { key: "Red", label: "Red", chinese: "紅色腰帶", bg: "bg-red-600", text: "text-white", dot: "bg-red-600" },
  { key: "Red 1 stripe", label: "Red 1 Stripe", chinese: "紅色一條", bg: "bg-red-600", text: "text-white", dot: "bg-red-600" },
  { key: "Red 2 stripes", label: "Red 2 Stripes", chinese: "紅色二條", bg: "bg-red-600", text: "text-white", dot: "bg-red-600" },
  { key: "Red 3 Stripes", label: "Red 3 Stripes", chinese: "紅色三條", bg: "bg-red-700", text: "text-white", dot: "bg-red-700" },
  { key: "Black", label: "Black Sash", chinese: "黑色腰帶", bg: "bg-gray-900", text: "text-white", dot: "bg-gray-900 border border-white/30" },
];

const PILLARS = [
  { key: "Chou Kup 初級 – Beginner", label: "Pillar 1: Chou Kup 初級", sub: "Beginner", color: "bg-gray-500", border: "border-gray-500", levels: [1, 2, 3] },
  { key: "Jung Kup 中級 – Intermediate", label: "Pillar 2: Jung Kup 中級", sub: "Intermediate", color: "bg-blue-600", border: "border-blue-600", levels: [4, 5, 6] },
  { key: "Gou Kup 高級 – Advanced", label: "Pillar 3: Gou Kup 高級", sub: "Advanced", color: "bg-orange-500", border: "border-orange-500", levels: [7, 8, 9] },
  { key: "Gau Lin – Instructor", label: "Pillar 4: Gau Lin", sub: "Instructor", color: "bg-purple-600", border: "border-purple-600", levels: [10, 11, 12] },
];

const LEVEL_NAMES = {
  1: "YAT KUP", 2: "YEE KUP", 3: "SAAM KUP",
  4: "SEI KUP", 5: "NG KUP", 6: "LOK KUP",
  7: "CHAT KUP", 8: "BAAT KUP", 9: "GAU KUP",
  10: "SUP KUP", 11: "SAP YAT KUP", 12: "SUP YEE KUP",
};

const getLevelNum = (lvl) => {
  if (!lvl) return 0;
  try { return parseInt(lvl.split(".")[0]); } catch { return 0; }
};

const calcTenure = (joinDate) => {
  if (!joinDate) return null;
  const start = new Date(joinDate);
  if (isNaN(start)) return null;
  const now = new Date();
  let years = now.getFullYear() - start.getFullYear();
  let months = now.getMonth() - start.getMonth();
  if (months < 0) { years--; months += 12; }
  if (years > 0) return `${years} Year${years > 1 ? 's' : ''}${months > 0 ? `, ${months} Mo` : ''}`;
  if (months > 0) return `${months} Month${months > 1 ? 's' : ''}`;
  return 'New';
};

const calcAge = (dob) => {
  if (!dob) return null;
  const birth = new Date(dob);
  if (isNaN(birth)) return null;
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  if (now.getMonth() < birth.getMonth() || (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate())) age--;
  return age > 0 ? age : null;
};

const formatLastSeen = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  const now = new Date();
  const diffDays = Math.floor((now - d) / (1000 * 60 * 60 * 24));
  const label = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  return { label, overdue: diffDays > 14 };
};

const CLASS_TAG_COLORS = {
  "Adults Kung Fu": "bg-red-700 text-white",
  "Adult": "bg-red-700 text-white",
  "Adults": "bg-red-700 text-white",
  "Tiger Cubs": "bg-orange-500 text-white",
  "Tiger": "bg-orange-500 text-white",
  "Little Dragons": "bg-blue-500 text-white",
  "Dragon": "bg-blue-500 text-white",
  "Private": "bg-purple-600 text-white",
  "Private Lesson": "bg-purple-600 text-white",
  "Remote/International": "bg-teal-600 text-white",
  "International/Remote": "bg-teal-600 text-white",
  "Remote": "bg-teal-600 text-white",
  "Tai Chi": "bg-teal-500 text-white",
};

const getClassTagColor = (cls) => {
  if (!cls) return "bg-white/10 text-white";
  for (const [key, val] of Object.entries(CLASS_TAG_COLORS)) {
    if (cls.toLowerCase().includes(key.toLowerCase())) return val;
  }
  return "bg-white/10 text-white";
};

const parseNoteHistory = (raw) => {
  if (!raw) return [];
  const entries = raw.split(/(?=\[)/g).filter(e => e.trim());
  if (entries.length === 0 && raw.trim()) return [{ date: "Legacy", text: raw.trim() }];
  return entries.map(e => {
    const m = e.match(/^\[([^\]]+)\]:\s*([\s\S]*)/);
    if (m) return { date: m[1], text: m[2].trim() };
    return { date: "Legacy", text: e.trim() };
  }).filter(e => e.text);
};

const formatTimestamp = () => {
  const d = new Date();
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
};

export default function SifuLedger() {
  const [mode, setMode] = useState("adults");
  const [students, setStudents] = useState([]);
  const [forms, setForms] = useState([]);
  // Summary data from /master-log/summary (lightweight, loaded once)
  const [attendanceSummary, setAttendanceSummary] = useState({}); // {student_id: {count, last_date}}
  const [formCountSummary, setFormCountSummary] = useState({}); // {student_id: count}
  const [formCountsByStyle, setFormCountsByStyle] = useState({}); // {student_id: {hung_kuen: N, tai_chi: N}}
  const [gradingEligibleSet, setGradingEligibleSet] = useState(new Set());
  // Per-student forms (loaded on-demand when student selected)
  const [studentForms, setStudentForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [updating, setUpdating] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [showGradingOnly, setShowGradingOnly] = useState(false);
  const [formTab, setFormTab] = useState("Hand Form");
  const [artStyle, setArtStyle] = useState("hung_kuen"); // v7.5: dual-art tabs

  // Dialogs
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedFormsToAdd, setSelectedFormsToAdd] = useState(new Set());
  const [addFormSearch, setAddFormSearch] = useState("");
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [editingNote, setEditingNote] = useState({ sfId: null, notes: "", formName: "", newNote: "" });
  const [savingNote, setSavingNote] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyEntries, setHistoryEntries] = useState([]);
  const [historyFormName, setHistoryFormName] = useState("");

  // Mastery Matrix
  const [showMatrix, setShowMatrix] = useState(false);
  const [matrixSearch, setMatrixSearch] = useState("");
  const [matrixFormId, setMatrixFormId] = useState(null);
  const [matrixAssignments, setMatrixAssignments] = useState([]); // loaded on-demand per form
  const matrixSearchRef = useRef(null);

  // Rank Deep Dive
  const [showRankDive, setShowRankDive] = useState(false);
  const [rankDiveTitle, setRankDiveTitle] = useState("");
  const [rankDiveStudents, setRankDiveStudents] = useState([]);

  // ========== LIGHTWEIGHT INITIAL LOAD ==========
  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [sRes, fRes, summRes] = await Promise.all([
        axios.get(`${API}/students`),
        axios.get(`${API}/forms`),
        axios.get(`${API}/master-log/summary`),
      ]);
      setStudents(sRes.data);
      setForms(fRes.data);
      const summ = summRes.data;
      setAttendanceSummary(summ.attendance || {});
      setFormCountSummary(summ.form_counts || {});
      setFormCountsByStyle(summ.form_counts_by_style || {});
      setGradingEligibleSet(new Set(summ.grading_eligible || []));
    } catch { toast.error("Failed to load data"); } finally { setLoading(false); }
  };

  // ========== ON-DEMAND: Load student forms when selected ==========
  const handleSelectStudent = useCallback(async (studentId) => {
    setSelectedId(studentId);
    setFormTab("Hand Form");
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/student-forms?student_id=${studentId}`);
      setStudentForms(res.data);
    } catch { toast.error("Failed to load student forms"); }
    finally { setDetailLoading(false); }
  }, []);

  // ========== ON-DEMAND: Load matrix data for a specific form ==========
  const handleMatrixFormSelect = useCallback(async (formId) => {
    setMatrixFormId(formId);
    try {
      const res = await axios.get(`${API}/student-forms?form_id=${formId}`);
      setMatrixAssignments(res.data);
    } catch { toast.error("Failed to load matrix data"); }
  }, []);

  const isAdult = useCallback((s) => {
    const ct = (s.class_type || '').toLowerCase();
    const cts = (s.class_types || []).map(c => c.toLowerCase()).join(' ');
    return (ct + ' ' + cts).includes('adult') || (!ct.includes('tiger') && !ct.includes('dragon') && (s.adult_pillar || s.adult_level));
  }, []);

  const getFormById = useCallback((fid) => forms.find(f => f.id === fid), [forms]);

  const filteredStudents = useMemo(() => {
    return students.filter(s => {
      const adult = isAdult(s);
      if (mode === "adults" && !adult) return false;
      if (mode === "kids" && adult) return false;
      if (search && !s.name.toLowerCase().includes(search.toLowerCase())) return false;
      if (showGradingOnly && !gradingEligibleSet.has(s.id)) return false;
      return true;
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [students, mode, search, showGradingOnly, isAdult, gradingEligibleSet]);

  const selected = students.find(s => s.id === selectedId);

  // Student forms for the currently selected student (filtered by art style)
  const groupedForms = useMemo(() => {
    const cats = artStyle === "tai_chi"
      ? { "Qigong/Foundation": [], "Hand Form": [], "Weapons": [] }
      : { "Hand Form": [], "Weapons": [], "Sparring Set": [] };
    studentForms.forEach(sf => {
      const form = getFormById(sf.form_id);
      if (!form) return;
      const fStyle = form.style || "hung_kuen";
      if (fStyle !== artStyle) return;
      const cat = form.category;
      if (cats[cat]) cats[cat].push({ ...sf, form });
      else if ((cat === "Qigong/Foundation" || cat === "Qigong") && artStyle === "tai_chi") cats["Qigong/Foundation"].push({ ...sf, form });
    });
    // Sort Tai Chi forms by level (L1→L10)
    if (artStyle === "tai_chi") {
      Object.keys(cats).forEach(k => {
        cats[k].sort((a, b) => (a.form?.level_category || 'Z').localeCompare(b.form?.level_category || 'Z'));
      });
    }
    return cats;
  }, [selected, studentForms, getFormById, artStyle]);

  // --- Pillar Progress Heat Map (uses on-demand studentForms) ---
  const pillarProgress = useMemo(() => {
    if (!selected) return [];
    return FORM_PILLARS.map(pillar => {
      const coreForms = studentForms.filter(sf => {
        const form = getFormById(sf.form_id);
        if (!form) return false;
        return getFormPillar(form) === pillar.key && (form.form_type || "Core Form") === "Core Form";
      });
      const proficient = coreForms.filter(sf => PROFICIENCY_CYCLE.indexOf(norm(sf.proficiency)) >= 2);
      const total = coreForms.length;
      const pct = total > 0 ? Math.round((proficient.length / total) * 100) : 0;
      return { ...pillar, total, proficient: proficient.length, pct };
    });
  }, [selected, studentForms, getFormById]);

  // --- Sash Progress Heat Map (Kids, uses on-demand studentForms) ---
  const sashProgress = useMemo(() => {
    if (!selected) return [];
    return SASH_LEVELS.map(sash => {
      const sashForms = studentForms.filter(sf => {
        const form = getFormById(sf.form_id);
        if (!form) return false;
        return parseFormBelts(form).includes(sash.key);
      });
      const proficient = sashForms.filter(sf => PROFICIENCY_CYCLE.indexOf(norm(sf.proficiency)) >= 2);
      const total = sashForms.length;
      const pct = total > 0 ? Math.round((proficient.length / total) * 100) : 0;
      return { ...sash, total, proficient: proficient.length, pct };
    });
  }, [selected, studentForms, getFormById]);

  // --- School Overview data ---
  const kidsStudents = useMemo(() => students.filter(s => !isAdult(s)), [students, isAdult]);
  const adultStudents = useMemo(() => students.filter(s => isAdult(s)), [students, isAdult]);

  const beltCounts = useMemo(() => {
    const counts = {};
    BELT_ORDER.forEach(b => { counts[b.key] = []; });
    kidsStudents.forEach(s => {
      const belt = s.belt_rank || "White";
      if (counts[belt]) counts[belt].push(s);
      else if (counts["White"]) counts["White"].push(s);
    });
    return counts;
  }, [kidsStudents]);

  const pillarData = useMemo(() => {
    const data = {};
    PILLARS.forEach(p => {
      data[p.key] = {};
      p.levels.forEach(l => { data[p.key][l] = []; });
    });
    const sifus = [];
    adultStudents.forEach(s => {
      if (s.is_sifu) sifus.push(s);
      const lvl = getLevelNum(s.adult_level);
      const pillar = PILLARS.find(p => p.levels.includes(lvl));
      if (pillar && data[pillar.key]?.[lvl]) data[pillar.key][lvl].push(s);
    });
    return { pillars: data, sifus };
  }, [adultStudents]);

  // --- Mastery Matrix data (uses on-demand matrixAssignments) ---
  const matrixFilteredForms = useMemo(() => {
    if (!matrixSearch) return forms;
    const q = matrixSearch.toLowerCase();
    return forms.filter(f =>
      f.english_name.toLowerCase().includes(q) ||
      f.cantonese.toLowerCase().includes(q) ||
      f.traditional_chinese.includes(matrixSearch)
    );
  }, [forms, matrixSearch]);

  const matrixData = useMemo(() => {
    if (!matrixFormId) return null;
    const columns = {};
    PROFICIENCY_CYCLE.forEach(p => { columns[p] = []; });
    matrixAssignments.forEach(sf => {
      const student = students.find(s => s.id === sf.student_id);
      if (student) {
        const stage = norm(sf.proficiency);
        columns[stage].push(student.name);
      }
    });
    const form = forms.find(f => f.id === matrixFormId);
    return { columns, form, total: matrixAssignments.length };
  }, [matrixFormId, matrixAssignments, students, forms]);

  // Auto-focus matrix search when modal opens
  useEffect(() => {
    if (showMatrix) {
      setTimeout(() => matrixSearchRef.current?.focus(), 100);
    } else {
      setMatrixSearch("");
      setMatrixFormId(null);
      setMatrixAssignments([]);
    }
  }, [showMatrix]);

  const handleProficiencyChange = async (sfId, newProf, studentId, formId) => {
    setUpdating(`${studentId}-${formId}`);
    try {
      await axios.put(`${API}/student-forms/${sfId}`, { proficiency: newProf });
      setStudentForms(prev => prev.map(sf => sf.id === sfId ? { ...sf, proficiency: newProf } : sf));
      toast.success(`Updated to ${newProf}`, { duration: 1500 });
    } catch { toast.error("Failed to update"); } finally { setUpdating(null); }
  };

  const handleAddForm = async () => {
    if (!selected || selectedFormsToAdd.size === 0) return;
    let added = 0;
    for (const formId of selectedFormsToAdd) {
      try {
        const res = await axios.post(`${API}/student-forms`, { student_id: selected.id, form_id: formId, proficiency: "Learning" });
        setStudentForms(prev => [...prev, res.data]);
        added++;
      } catch { /* skip duplicates */ }
    }
    if (added > 0) {
      toast.success(`${added} form${added > 1 ? 's' : ''} added`);
      // Update form count summary locally
      setFormCountSummary(prev => ({ ...prev, [selected.id]: (prev[selected.id] || 0) + added }));
    }
    setShowAddForm(false);
    setSelectedFormsToAdd(new Set());
  };

  const getAvailableForms = () => {
    if (!selected) return {};
    const assigned = studentForms.map(sf => sf.form_id);
    const available = forms.filter(f => !assigned.includes(f.id) && (f.style || "hung_kuen") === artStyle);
    const grouped = {};
    available.forEach(f => { 
      const cat = f.category || "Hand Form";
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(f); 
    });
    return grouped;
  };

  const toggleFormSelection = (formId) => {
    setSelectedFormsToAdd(prev => {
      const next = new Set(prev);
      if (next.has(formId)) next.delete(formId); else next.add(formId);
      return next;
    });
  };

  const openNotesDialog = (sf, form) => {
    setEditingNote({ sfId: sf.id, notes: sf.notes || "", formName: form?.english_name || "Form", newNote: "" });
    setShowNotesDialog(true);
  };

  const handleSaveNotes = async () => {
    if (!editingNote.sfId || !editingNote.newNote.trim()) return;
    setSavingNote(true);
    const stamp = `[${formatTimestamp()}]: ${editingNote.newNote.trim()}`;
    const updated = editingNote.notes ? `${stamp}\n${editingNote.notes}` : stamp;
    try {
      await axios.put(`${API}/student-forms/${editingNote.sfId}`, { notes: updated });
      setStudentForms(prev => prev.map(sf => sf.id === editingNote.sfId ? { ...sf, notes: updated } : sf));
      toast.success("Note saved");
      setShowNotesDialog(false);
    } catch { toast.error("Failed to save"); } finally { setSavingNote(false); }
  };

  const openHistory = (sf, form) => {
    setHistoryFormName(form?.english_name || "Form");
    setHistoryEntries(parseNoteHistory(sf.notes));
    setShowHistory(true);
  };

  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const handleDeleteForm = async (sfId) => {
    try {
      await axios.delete(`${API}/student-forms/${sfId}`);
      toast.success("Form removed from student");
      setDeleteConfirm(null);
      if (selected) {
        const res = await axios.get(`${API}/student-forms?student_id=${selected.id}`);
        setStudentForms(res.data);
      }
    } catch { toast.error("Failed to delete form"); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="text-[#A1A1AA]">Loading Master Log...</div></div>;

  return (
    <div data-testid="sifu-ledger" className="animate-fadeIn">
      {/* v7.5: Primary Art Style Tabs — Top Level */}
      <div className="flex gap-0 mb-4">
        <button data-testid="art-hung-kuen"
          onClick={() => { setArtStyle("hung_kuen"); setSelectedId(null); setFormTab("Hand Form"); }}
          className={`flex-1 py-3 text-base font-bold uppercase tracking-widest transition-all ${artStyle === "hung_kuen" ? "bg-[#D92323] text-white border-b-2 border-[#D4AF37]" : "bg-white/5 text-[#A1A1AA] hover:bg-white/10"}`}
          style={{ fontFamily: 'Oswald, sans-serif' }}>
          Hung Kuen
        </button>
        <button data-testid="art-tai-chi"
          onClick={() => { setArtStyle("tai_chi"); setSelectedId(null); setFormTab("Hand Form"); }}
          className={`flex-1 py-3 text-base font-bold uppercase tracking-widest transition-all ${artStyle === "tai_chi" ? "bg-teal-600 text-white border-b-2 border-teal-300" : "bg-white/5 text-[#A1A1AA] hover:bg-white/10"}`}
          style={{ fontFamily: 'Oswald, sans-serif' }}>
          Tai Chi
        </button>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h1 data-testid="master-log-title" className="text-2xl sm:text-3xl font-bold text-[#D4AF37] uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
            The Master Log <span className="text-xl sm:text-2xl">師父總錄</span>
          </h1>
          <p className="text-[#A1A1AA] text-sm mt-0.5">Form Mastery & Grading Tracker</p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="mastery-matrix-btn" size="sm" onClick={() => setShowMatrix(true)}
            className="bg-white/10 text-[#A1A1AA] hover:bg-white/20 hover:text-white">
            <LayoutGrid className="w-4 h-4 mr-1.5" />Mastery Matrix
          </Button>
          <Button data-testid="show-grading-btn" size="sm" onClick={() => setShowGradingOnly(!showGradingOnly)}
            className={showGradingOnly ? "bg-[#D4AF37] text-black hover:bg-[#b8962e]" : "bg-white/10 text-[#A1A1AA] hover:bg-white/20"}>
            <Star className="w-4 h-4 mr-1" />Grading Candidates
          </Button>
        </div>
      </div>

      {artStyle === "hung_kuen" ? (
      <div className="flex gap-4" style={{ height: 'calc(100vh - 230px)' }}>
        {/* Sidebar */}
        <div className={`${selectedId ? 'hidden lg:flex' : 'flex'} flex-col w-full lg:w-80 flex-shrink-0`}>
          <div className="flex gap-2 mb-2">
            <Button data-testid="filter-kids" size="sm" onClick={() => { setMode("kids"); setSelectedId(null); }}
              className={`flex-1 ${mode === "kids" ? "bg-orange-500 text-white hover:bg-orange-600" : "bg-white/10 text-[#A1A1AA] hover:bg-white/20"}`}>
              Kids
            </Button>
            <Button data-testid="filter-adults" size="sm" onClick={() => { setMode("adults"); setSelectedId(null); }}
              className={`flex-1 ${mode === "adults" ? "bg-[#D92323] text-white hover:bg-red-700" : "bg-white/10 text-[#A1A1AA] hover:bg-white/20"}`}>
              Adults
            </Button>
          </div>
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
            <Input data-testid="student-search" value={search} onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-[#18181b] border-white/10 text-white h-10" placeholder="Search students..." />
          </div>
          <div className="flex-1 overflow-y-auto space-y-1 pr-1">
            {filteredStudents.length === 0 ? (
              <p className="text-[#A1A1AA] text-center py-8 text-sm">No students found</p>
            ) : filteredStudents.map(s => {
              const att = attendanceSummary[s.id]?.count || 0;
              const sfCount = (formCountsByStyle[s.id] || {})['hung_kuen'] || 0;
              const eligible = gradingEligibleSet.has(s.id);
              const active = selectedId === s.id;
              return (
                <div key={s.id} data-testid={`student-row-${s.id}`}
                  onClick={() => handleSelectStudent(s.id)}
                  className={`flex items-center gap-3 p-3 rounded-sm cursor-pointer transition-all ${
                    active
                      ? 'bg-[#D4AF37]/20 border-2 border-[#D4AF37] shadow-[0_0_12px_rgba(212,175,55,0.15)]'
                      : 'bg-[#18181b] border-2 border-transparent hover:bg-[#1f1f23] hover:border-white/10'
                  }`}>
                  <div className={`w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0 ${s.is_sifu ? 'bg-gradient-to-br from-[#D4AF37] to-[#b8962e]' : isAdult(s) ? 'bg-[#D92323]' : (s.class_type || '').includes('Tiger') ? 'bg-orange-500' : 'bg-blue-500'}`}>
                    <span className={`font-bold text-sm ${s.is_sifu ? 'text-black' : 'text-white'}`}>{s.is_sifu ? '師' : s.name?.charAt(0)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className={`text-sm font-semibold truncate ${active ? 'text-[#D4AF37]' : 'text-white'}`}>{s.name}</span>
                      {eligible && <Star className="w-3.5 h-3.5 text-[#D4AF37] flex-shrink-0 fill-[#D4AF37]" />}
                      {s.billing_type === "exempt" && <Star className="w-3.5 h-3.5 text-[#D4AF37] flex-shrink-0 fill-[#D4AF37]" title="Exempt" />}
                      {s.billing_type === "pay_as_you_go" && <Coins className="w-3.5 h-3.5 text-[#14b8a6] flex-shrink-0" title="PAYG" />}
                    </div>
                    <div className="flex items-center gap-2 text-[11px] text-[#A1A1AA]">
                      <span>{att} sessions</span>
                      <span>·</span>
                      <span>{sfCount} forms</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Workspace */}
        <div className={`${selectedId ? 'flex' : 'hidden lg:flex'} flex-col flex-1 min-w-0`}>
          {!selectedId ? (
            /* ========== LIVE SCHOOL OVERVIEW ========== */
            <div data-testid="school-overview" className="flex-1 overflow-y-auto pr-1 space-y-4">
              <h2 className="text-white text-lg font-bold uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
                Live School Overview
              </h2>

              {/* Section A: Kids Grading */}
              <Card className="bg-[#18181b] border-white/10 rounded-sm">
                <CardContent className="p-4">
                  <h3 className="text-white font-bold uppercase tracking-wide text-sm mb-3" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    Kids Grading — Belt System
                  </h3>
                  <div className="space-y-1">
                    {BELT_ORDER.map(belt => {
                      const list = beltCounts[belt.key] || [];
                      return (
                        <div key={belt.key} data-testid={`belt-row-${belt.key}`}
                          onClick={() => { setRankDiveTitle(`${belt.label} Sash — ${belt.chinese}`); setRankDiveStudents(list); setShowRankDive(true); }}
                          className="flex items-center gap-3 py-1.5 px-2 rounded-sm hover:bg-white/8 transition-colors cursor-pointer group">
                          <div className={`w-5 h-5 rounded-sm flex-shrink-0 ${belt.dot}`} />
                          <span className="text-white text-sm font-medium flex-1 group-hover:text-[#D4AF37] transition-colors">{belt.label}</span>
                          <span className="text-[#A1A1AA] text-xs mr-2">{belt.chinese}</span>
                          <span className={`text-sm font-bold min-w-[28px] text-right ${list.length > 0 ? 'text-orange-400' : 'text-[#555]'}`}
                            style={{ fontFamily: 'Oswald, sans-serif' }}>
                            {list.length}
                          </span>
                        </div>
                      );
                    })}
                    <div className="flex items-center justify-between pt-2 mt-2 border-t border-white/10 px-2">
                      <span className="text-[#A1A1AA] text-xs uppercase">Total Kids</span>
                      <span className="text-white font-bold" style={{ fontFamily: 'Oswald, sans-serif' }}>{kidsStudents.length}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Section B: Adults Grading */}
              <Card className="bg-[#18181b] border-white/10 rounded-sm">
                <CardContent className="p-4">
                  <h3 className="text-white font-bold uppercase tracking-wide text-sm mb-3" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    Adults Grading — 4 Pillars
                  </h3>
                  <div className="space-y-4">
                    {PILLARS.map(pillar => {
                      const pData = pillarData.pillars[pillar.key] || {};
                      const totalInPillar = pillar.levels.reduce((sum, l) => sum + (pData[l]?.length || 0), 0);
                      return (
                        <div key={pillar.key} data-testid={`pillar-${pillar.key}`}>
                          <div className={`flex items-center gap-2 mb-2 pb-1 border-b ${pillar.border}/40`}>
                            <div className={`w-3 h-3 rounded-sm ${pillar.color}`} />
                            <span className="text-white text-sm font-bold">{pillar.label}</span>
                            <span className="text-[#A1A1AA] text-xs">— {pillar.sub}</span>
                            <span className="ml-auto text-white font-bold text-sm" style={{ fontFamily: 'Oswald, sans-serif' }}>{totalInPillar}</span>
                          </div>
                          <div className="grid grid-cols-3 gap-1 ml-5">
                            {pillar.levels.map(lvl => {
                              const lvlStudents = pData[lvl] || [];
                              const count = lvlStudents.length;
                              return (
                                <div key={lvl}
                                  onClick={() => { setRankDiveTitle(`${lvl}. ${LEVEL_NAMES[lvl]}`); setRankDiveStudents(lvlStudents); setShowRankDive(true); }}
                                  className="flex items-center justify-between py-1.5 px-2 rounded-sm bg-white/3 hover:bg-white/8 cursor-pointer group transition-colors">
                                  <span className="text-white text-xs font-bold group-hover:text-[#D4AF37] transition-colors">{lvl}. {LEVEL_NAMES[lvl]}</span>
                                  <span className={`text-xs font-bold min-w-[20px] text-right ${count > 0 ? 'text-orange-400' : 'text-[#555]'}`}
                                    style={{ fontFamily: 'Oswald, sans-serif' }}>
                                    {count}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}

                    {/* Sifu Rank */}
                    <div data-testid="sifu-rank-section" className="mt-2 pt-3 border-t-2 border-[#D4AF37]/50">
                      <div className="flex items-center gap-3 px-2 py-2 rounded-sm bg-[#D4AF37]/10 border border-[#D4AF37]/30">
                        <Award className="w-6 h-6 text-[#D4AF37] flex-shrink-0" />
                        <div className="flex-1">
                          <span className="text-[#D4AF37] font-bold text-base" style={{ fontFamily: 'Oswald, sans-serif' }}>
                            13. Sifu 師父
                          </span>
                          <span className="text-[#D4AF37]/70 text-xs ml-2">Ultimate Rank</span>
                        </div>
                        <span className="text-[#D4AF37] font-bold text-lg" style={{ fontFamily: 'Oswald, sans-serif' }}>
                          {pillarData.sifus.length}
                        </span>
                      </div>
                      {pillarData.sifus.length > 0 && (
                        <div className="mt-2 ml-9 space-y-1">
                          {pillarData.sifus.map(s => (
                            <span key={s.id} className="inline-block mr-2 mb-1 px-2 py-0.5 text-xs rounded-sm bg-[#D4AF37]/20 text-[#D4AF37] font-medium border border-[#D4AF37]/30">
                              {s.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between pt-2 mt-2 border-t border-white/10 px-2">
                      <span className="text-[#A1A1AA] text-xs uppercase">Total Adults</span>
                      <span className="text-white font-bold" style={{ fontFamily: 'Oswald, sans-serif' }}>{adultStudents.length}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            /* ========== STUDENT DETAIL VIEW ========== */
            <>
              <Card className="bg-[#18181b] border-white/10 rounded-sm mb-3 flex-shrink-0">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Button size="sm" variant="ghost" className="lg:hidden text-[#A1A1AA]" onClick={() => setSelectedId(null)}>
                        <ArrowLeft className="w-4 h-4" />
                      </Button>
                      <div className={`w-11 h-11 rounded-sm flex items-center justify-center ${selected.is_sifu ? 'bg-gradient-to-br from-[#D4AF37] to-[#b8962e]' : isAdult(selected) ? 'bg-[#D92323]' : (selected.class_type || '').includes('Tiger') ? 'bg-orange-500' : 'bg-blue-500'}`}>
                        <span className={`font-bold text-lg ${selected.is_sifu ? 'text-black' : 'text-white'}`}>{selected.is_sifu ? '師' : selected.name?.charAt(0)}</span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h2 className="text-white text-lg font-semibold">{selected.name}</h2>
                          {selected.is_sifu && <span className="px-2 py-0.5 text-xs rounded-sm bg-[#D4AF37] text-black font-bold">SIFU</span>}
                          {gradingEligibleSet.has(selected.id) && <Star className="w-4 h-4 text-[#D4AF37] fill-[#D4AF37]" />}
                          {selected.billing_type === "exempt" && <Star className="w-4 h-4 text-[#D4AF37] fill-[#D4AF37]" title="Exempt" />}
                          {selected.billing_type === "pay_as_you_go" && <Coins className="w-4 h-4 text-[#14b8a6]" title="Pay-As-You-Go" />}
                          <div className="flex items-center gap-1 ml-1" title="Group Sessions">
                            <Users className="w-3.5 h-3.5 text-blue-400" />
                            <span className="text-blue-400 text-xs font-bold">{attendanceSummary[selected.id]?.count || 0}</span>
                          </div>
                          <div className="flex items-center gap-1" title="Forms Assigned">
                            <BookOpen className="w-3.5 h-3.5 text-green-400" />
                            <span className="text-green-400 text-xs font-bold">{studentForms.length}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <Button size="sm" onClick={() => { setSelectedFormsToAdd(new Set()); setAddFormSearch(""); setShowAddForm(true); }} className="bg-[#D4AF37] hover:bg-[#b8962e] text-black">
                      <Plus className="w-4 h-4 mr-1" />Add Form
                    </Button>
                  </div>
                  {/* Info Bar */}
                  <div data-testid="student-info-bar" className="grid grid-cols-2 lg:grid-cols-5 gap-2">
                    <div className="bg-white/5 rounded-sm px-3 py-2">
                      <p className="text-[#A1A1AA] text-[10px] uppercase tracking-wider mb-0.5">Rank</p>
                      {isAdult(selected) ? (
                        <p className="text-white text-xs font-bold leading-tight">
                          {selected.adult_level || '—'}
                          {selected.adult_pillar && <span className="text-[#A1A1AA] font-normal block text-[10px]">{selected.adult_pillar.split(" – ")[0]}</span>}
                        </p>
                      ) : (
                        <p className="text-white text-xs font-bold">{selected.belt_rank || '—'}</p>
                      )}
                    </div>
                    <div className="bg-white/5 rounded-sm px-3 py-2">
                      <p className="text-[#A1A1AA] text-[10px] uppercase tracking-wider mb-0.5">Tenure</p>
                      <p className="text-white text-xs font-bold">{calcTenure(selected.join_date) || '—'}</p>
                      {selected.join_date && <p className="text-[#A1A1AA] text-[10px]">Since {new Date(selected.join_date).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}</p>}
                    </div>
                    <div className="bg-white/5 rounded-sm px-3 py-2">
                      <p className="text-[#A1A1AA] text-[10px] uppercase tracking-wider mb-0.5">Bio</p>
                      <p className="text-white text-xs font-bold">
                        {[selected.gender, calcAge(selected.date_of_birth) ? `Age ${calcAge(selected.date_of_birth)}` : null].filter(Boolean).join(', ') || '—'}
                      </p>
                    </div>
                    <div className="bg-white/5 rounded-sm px-3 py-2">
                      <p className="text-[#A1A1AA] text-[10px] uppercase tracking-wider mb-0.5">Last Seen</p>
                      {(() => {
                        const ls = formatLastSeen(attendanceSummary[selected.id]?.last_date);
                        if (!ls) return <p className="text-[#A1A1AA] text-xs">No sessions</p>;
                        return <p className={`text-xs font-bold ${ls.overdue ? 'text-red-500' : 'text-white'}`}>{ls.label}</p>;
                      })()}
                    </div>
                    <div className="bg-white/5 rounded-sm px-3 py-2 col-span-2 lg:col-span-1">
                      <p className="text-[#A1A1AA] text-[10px] uppercase tracking-wider mb-1">Schedule</p>
                      <div className="flex flex-wrap gap-1">
                        {(() => {
                          const classes = selected.class_types?.length > 0
                            ? selected.class_types
                            : selected.class_type ? [selected.class_type] : [];
                          if (classes.length === 0) return <span className="text-[#A1A1AA] text-xs">—</span>;
                          return classes.map((c, i) => (
                            <span key={i} className={`px-1.5 py-0.5 rounded-sm text-[10px] font-semibold ${getClassTagColor(c)}`}>{c}</span>
                          ));
                        })()}
                      </div>
                    </div>
                  </div>
                  {/* Progress Heat Maps — show loading or data */}
                  {detailLoading ? (
                    <div className="mt-3 text-center text-[#A1A1AA] text-xs py-2">Loading progress data...</div>
                  ) : (() => {
                    const adult = isAdult(selected);
                    const hasPillar = pillarProgress.some(p => p.total > 0);
                    const hasSash = sashProgress.some(p => p.total > 0);
                    const pillarSection = hasPillar && (
                      <div data-testid="pillar-progress" className="mt-2">
                        <p className="text-[9px] text-[#A1A1AA] uppercase tracking-wider font-semibold mb-1">Adult Pillar Progress</p>
                        <div className="flex gap-2">
                          {pillarProgress.map(p => (
                            <div key={p.key} className="flex-1 bg-white/3 rounded-sm px-2 py-1.5">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-[10px] text-[#A1A1AA] font-semibold">{p.label} {p.full}</span>
                                <span className={`text-[10px] font-bold ${p.pct >= 80 ? 'text-green-400' : p.pct >= 40 ? 'text-orange-400' : 'text-[#A1A1AA]'}`}>{p.pct}%</span>
                              </div>
                              <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                                <div className={`h-full ${p.color} rounded-full transition-all`} style={{ width: `${p.pct}%` }} />
                              </div>
                              <p className="text-[9px] text-[#555] mt-0.5">{p.proficient}/{p.total} core</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                    const sashSection = hasSash && (
                      <div data-testid="sash-progress" className="mt-2">
                        <p className="text-[9px] text-[#A1A1AA] uppercase tracking-wider font-semibold mb-1">Kids Sash Progress</p>
                        <div className="flex gap-1 flex-wrap">
                          {sashProgress.filter(s => s.total > 0).map(s => (
                            <div key={s.key} className="bg-white/3 rounded-sm px-2 py-1.5 min-w-[70px]">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-[10px] text-[#A1A1AA] font-semibold">{s.label}</span>
                                <span className={`text-[10px] font-bold ${s.pct >= 80 ? 'text-green-400' : s.pct >= 40 ? 'text-orange-400' : 'text-[#A1A1AA]'}`}>{s.pct}%</span>
                              </div>
                              <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                                <div className={`h-full ${s.color} rounded-full transition-all`} style={{ width: `${s.pct}%` }} />
                              </div>
                              <p className="text-[9px] text-[#555] mt-0.5">{s.proficient}/{s.total}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                    return adult ? <>{pillarSection}</> : <>{sashSection}</>;
                  })()}
                </CardContent>
              </Card>

              {detailLoading ? (
                <div className="flex items-center justify-center py-12 text-[#A1A1AA]">Loading forms...</div>
              ) : (
                <Tabs value={formTab} onValueChange={setFormTab} className="flex-1 flex flex-col min-h-0">

                  {/* v7.5: Gold Tier Readiness for Tai Chi */}
                  {artStyle === "tai_chi" && (() => {
                    const taiChiForms = studentForms.filter(sf => {
                      const form = getFormById(sf.form_id);
                      return form && form.style === "tai_chi" && form.min_months;
                    });
                    if (taiChiForms.length === 0) return null;
                    const readyForms = taiChiForms.filter(sf => {
                      const form = getFormById(sf.form_id);
                      if (!form || !form.min_months) return false;
                      const lastGrading = selected?.last_grading_date || selected?.join_date;
                      if (!lastGrading) return false;
                      const monthsSince = Math.floor((new Date() - new Date(lastGrading)) / (1000 * 60 * 60 * 24 * 30));
                      return monthsSince >= form.min_months;
                    });
                    if (readyForms.length === 0) return null;
                    return (
                      <div data-testid="gold-readiness" className="mb-2 p-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-sm">
                        <p className="text-[#D4AF37] text-xs font-bold uppercase tracking-wider flex items-center gap-1">
                          <span className="text-base">🏆</span> Gold Tier Readiness — {readyForms.length} form{readyForms.length > 1 ? 's' : ''} eligible
                        </p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {readyForms.map(sf => {
                            const form = getFormById(sf.form_id);
                            return (
                              <span key={sf.id} className="text-[10px] text-[#D4AF37] bg-[#D4AF37]/10 px-1.5 py-0.5 rounded-sm">
                                {form?.gate_info} — {form?.english_name}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}

                  <TabsList className="bg-[#18181b] border border-white/10 rounded-sm p-1 flex-shrink-0">
                    {Object.keys(groupedForms).map(cat => (
                      <TabsTrigger key={cat} value={cat} className="flex-1 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black text-[#A1A1AA] text-sm">
                        {cat} ({groupedForms[cat].length})
                      </TabsTrigger>
                    ))}
                  </TabsList>

                  {Object.keys(groupedForms).map(cat => (
                    <TabsContent key={cat} value={cat} className="flex-1 overflow-y-auto mt-3 space-y-2 pr-1">
                      {groupedForms[cat].length === 0 ? (
                        <div className="flex items-center justify-center py-12 text-[#A1A1AA]">
                          <p>No {cat.toLowerCase()} forms assigned</p>
                        </div>
                      ) : groupedForms[cat].map(sf => {
                        const profKey = norm(sf.proficiency);
                        const isUp = updating === `${selected.id}-${sf.form_id}`;
                        return (
                          <div key={sf.id} className="bg-[#0f0f10] rounded-sm border border-white/5 p-3">
                            <div className="flex items-start justify-between mb-2">
                              <div className="min-w-0">
                                <p className="text-white font-semibold text-[15px]">
                                  {sf.form?.english_name || 'Unknown'}
                                  {sf.form?.gate_info && <span className="ml-2 text-teal-400 text-[10px] font-bold px-1.5 py-0.5 bg-teal-500/10 border border-teal-500/20 rounded-sm">{sf.form.gate_info}</span>}
                                </p>
                                <p className="text-[#A1A1AA] text-xs">{sf.form?.cantonese || ''} | {sf.form?.traditional_chinese || ''}</p>
                                {sf.form?.skill_focus && <p className="text-teal-300 text-[10px] mt-0.5">{sf.form.skill_focus}{sf.form.min_months ? ` · ${sf.form.min_months} months min` : ''}</p>}
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <Button size="sm" variant="ghost" onClick={() => openHistory(sf, sf.form)}
                                  className="text-[#A1A1AA] hover:text-white p-2" title="Note history">
                                  <Clock className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => openNotesDialog(sf, sf.form)}
                                  className={`p-2 ${sf.notes ? 'text-[#D4AF37]' : 'text-[#A1A1AA]'} hover:text-white`} title="Add note">
                                  <FileText className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="ghost" data-testid={`delete-form-${sf.id}`}
                                  onClick={() => setDeleteConfirm({ id: sf.id, name: sf.form?.english_name || 'this form' })}
                                  className="text-[#555] hover:text-red-400 p-2" title="Remove form">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                            {/* 6-Stage Segmented Toggle */}
                            <div data-testid={`proficiency-toggle-${sf.id}`} className="flex gap-1">
                              {PROFICIENCY_LEVELS.map((level) => {
                                const active = profKey === level.key;
                                return (
                                  <button key={level.key} disabled={isUp}
                                    onClick={() => handleProficiencyChange(sf.id, level.key, selected.id, sf.form_id)}
                                    className={`flex-1 py-2.5 text-xs font-semibold rounded-sm transition-all ${
                                      active
                                        ? `${level.activeColor} ${level.textColor} ring-2 ring-white/50 shadow-lg`
                                        : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10'
                                    } ${isUp ? 'opacity-50' : ''}`}
                                    title={level.key}>
                                    <span className="hidden sm:inline">{level.key}</span>
                                    <span className="sm:hidden">{level.short}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </TabsContent>
                  ))}
                </Tabs>
              )}
            </>
          )}
        </div>
      </div>
      ) : (
      /* ========== TAI CHI VIEW ========== */
      <div data-testid="tai-chi-view" style={{ height: 'calc(100vh - 230px)' }}>
        <div className="flex gap-4 h-full">
          {/* Tai Chi Student Sidebar */}
          <div className={`${selectedId ? 'hidden lg:flex' : 'flex'} flex-col w-full lg:w-80 flex-shrink-0`}>
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
              <Input data-testid="tc-student-search" value={search} onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-[#18181b] border-white/10 text-white h-10" placeholder="Search Tai Chi students..." />
            </div>
            <div className="flex-1 overflow-y-auto space-y-1 pr-1">
              {students.filter(s => {
                const classes = s.class_types || [s.class_type || ''];
                const isTaiChi = classes.some(c => c.toLowerCase().includes('tai chi'));
                return isTaiChi && s.name?.toLowerCase().includes(search.toLowerCase());
              }).map(s => {
                const att = attendanceSummary[s.id]?.count || 0;
                const sfCount = (formCountsByStyle[s.id] || {})['tai_chi'] || 0;
                const active = selectedId === s.id;
                return (
                  <div key={s.id} data-testid={`tc-student-${s.id}`}
                    onClick={() => handleSelectStudent(s.id)}
                    className={`flex items-center gap-3 p-3 rounded-sm cursor-pointer transition-all ${
                      active
                        ? 'bg-teal-600/20 border-2 border-teal-500 shadow-[0_0_12px_rgba(20,184,166,0.15)]'
                        : 'bg-[#18181b] border-2 border-transparent hover:bg-[#1f1f23] hover:border-white/10'
                    }`}>
                    <div className="w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0 bg-teal-600">
                      <span className="font-bold text-sm text-white">{s.name?.charAt(0)}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className={`text-sm font-semibold truncate block ${active ? 'text-teal-400' : 'text-white'}`}>{s.name}</span>
                      <div className="flex items-center gap-2 text-[11px] text-[#A1A1AA]">
                        <span>{att} sessions</span>
                        <span>·</span>
                        <span>{sfCount} forms</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Tai Chi Workspace */}
          <div className={`${selectedId ? 'flex' : 'hidden lg:flex'} flex-col flex-1 min-w-0`}>
            {!selectedId ? (
              /* Tai Chi Overview */
              <div className="flex-1 overflow-y-auto">
                <Card className="bg-[#18181b] border-white/10 rounded-sm mb-4">
                  <CardContent className="p-6">
                    <h2 className="text-xl font-bold text-teal-400 uppercase tracking-wider mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>
                      Tai Chi 4-10-8 Curriculum Map
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                      {[
                        { pillar: "P1: Beginner (Chou Kup)", color: "bg-teal-500/10 border-teal-500/30", levels: [
                          { l: "L1", gate: "Wuji (無極)", focus: "Foundations/Qigong", time: "4 Months" },
                          { l: "L2", gate: "Gate 1 ☰ Kin", focus: "Structure/Alignment", time: "4 Months" },
                          { l: "L3", gate: "Gate 2 ☱ Dui", focus: "Relaxation (Song)", time: "4 Months" },
                          { l: "L4", gate: "Gate 3 ☲ Lei", focus: "Spirit/Expression", time: "4 Months" },
                        ]},
                        { pillar: "P2: Intermediate (Jung Kup)", color: "bg-blue-500/10 border-blue-500/30", levels: [
                          { l: "L5", gate: "Gate 4 ☳ Jan", focus: "Fajin (Power)", time: "6 Months" },
                          { l: "L6", gate: "Gate 5 ☴ Sun", focus: "Flow/Agility", time: "6 Months" },
                          { l: "L7", gate: "Gate 6 ☵ Ham", focus: "Adaptability", time: "6 Months" },
                        ]},
                        { pillar: "P3: Advanced (Gou Kup)", color: "bg-purple-500/10 border-purple-500/30", levels: [
                          { l: "L8", gate: "Gate 7 ☶ Gan", focus: "Rooting/Stability", time: "12 Months" },
                          { l: "L9", gate: "Gate 8 ☷ Kwun", focus: "Total Integration", time: "12 Months" },
                        ]},
                        { pillar: "P4: Instructor", color: "bg-[#D4AF37]/10 border-[#D4AF37]/30", levels: [
                          { l: "L10", gate: "☯️ Tai Gik Mun", focus: "Unity/Mastery", time: "12-18 Months" },
                        ]},
                      ].map(p => (
                        <div key={p.pillar} className={`${p.color} border rounded-sm p-3`}>
                          <h3 className="text-white text-xs font-bold uppercase tracking-wider mb-2" style={{ fontFamily: 'Oswald, sans-serif' }}>{p.pillar}</h3>
                          <div className="space-y-2">
                            {p.levels.map(lv => (
                              <div key={lv.l} className="bg-black/20 rounded-sm p-2">
                                <div className="flex items-center justify-between">
                                  <span className="text-teal-400 text-xs font-bold">{lv.l}</span>
                                  <span className="text-[#A1A1AA] text-[10px]">{lv.time}</span>
                                </div>
                                <p className="text-white text-xs mt-0.5">{lv.gate}</p>
                                <p className="text-[#A1A1AA] text-[10px]">{lv.focus}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Tai Chi Forms Summary */}
                <Card className="bg-[#18181b] border-white/10 rounded-sm">
                  <CardContent className="p-6">
                    <h2 className="text-lg font-bold text-teal-400 uppercase tracking-wider mb-3" style={{ fontFamily: 'Oswald, sans-serif' }}>
                      Tai Chi Forms ({forms.filter(f => f.style === 'tai_chi').length})
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {forms.filter(f => f.style === 'tai_chi').sort((a,b) => (a.level_category||'').localeCompare(b.level_category||'')).map(f => (
                        <div key={f.id} className="flex items-center gap-2 p-2 bg-white/5 rounded-sm">
                          <span className="text-teal-400 text-[10px] font-bold bg-teal-500/10 px-1.5 py-0.5 rounded-sm flex-shrink-0">{f.level_category || '?'}</span>
                          <div className="min-w-0">
                            <p className="text-white text-xs font-medium truncate">{f.english_name}</p>
                            <p className="text-[#A1A1AA] text-[10px] truncate">{f.gate_info}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              /* Tai Chi Student Detail - reuses the form tabs with tai_chi filter */
              <>
                <div className="flex items-center gap-3 mb-4">
                  <Button size="sm" variant="ghost" onClick={() => setSelectedId(null)} className="text-[#A1A1AA] hover:text-white p-1 lg:hidden">
                    <ArrowLeft className="w-5 h-5" />
                  </Button>
                  <div className="w-10 h-10 rounded-sm flex items-center justify-center bg-teal-600">
                    <span className="text-white font-bold">{selected?.name?.charAt(0)}</span>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-white text-lg font-bold" style={{ fontFamily: 'Oswald, sans-serif' }}>{selected?.name}</h2>
                    <p className="text-[#A1A1AA] text-xs">Tai Chi Progress</p>
                  </div>
                  <Button data-testid="tc-add-form-btn" size="sm" onClick={() => setShowAddForm(true)}
                    className="bg-teal-600 text-white hover:bg-teal-700 text-xs">
                    <Plus className="w-4 h-4 mr-1" /> Add Form
                  </Button>
                </div>

                {/* Gold Tier Readiness */}
                {(() => {
                  const readyForms = studentForms.filter(sf => {
                    const form = getFormById(sf.form_id);
                    if (!form || form.style !== 'tai_chi' || !form.min_months) return false;
                    // Skill check: must be Proficient or above
                    const prof = norm(sf.proficiency || "Learning");
                    const profIdx = PROFICIENCY_CYCLE.indexOf(prof);
                    if (profIdx < 2) return false; // Must be Proficient (idx 2) or higher
                    // Time check: min_months met
                    const lastGrading = selected?.last_grading_date || selected?.join_date;
                    if (!lastGrading) return false;
                    const monthsSince = Math.floor((new Date() - new Date(lastGrading)) / (1000 * 60 * 60 * 24 * 30));
                    return monthsSince >= form.min_months;
                  });
                  if (readyForms.length === 0) return null;
                  return (
                    <div data-testid="gold-readiness" className="mb-3 p-3 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-sm">
                      <p className="text-[#D4AF37] text-xs font-bold uppercase tracking-wider flex items-center gap-1">
                        <span className="text-base">🏆</span> Gold Tier — {readyForms.length} form{readyForms.length > 1 ? 's' : ''} eligible for advancement
                      </p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {readyForms.map(sf => {
                          const form = getFormById(sf.form_id);
                          return <span key={sf.id} className="text-[10px] text-[#D4AF37] bg-[#D4AF37]/10 px-1.5 py-0.5 rounded-sm">{form?.gate_info} — {form?.english_name}</span>;
                        })}
                      </div>
                    </div>
                  );
                })()}

                <Tabs value={formTab} onValueChange={setFormTab} className="flex-1 flex flex-col min-h-0">
                  <TabsList className="bg-[#18181b] border border-white/10 rounded-sm p-1 flex-shrink-0">
                    {Object.keys(groupedForms).map(cat => (
                      <TabsTrigger key={cat} value={cat} className="flex-1 data-[state=active]:bg-teal-600 data-[state=active]:text-white text-[#A1A1AA] text-sm">
                        {cat} ({groupedForms[cat].length})
                      </TabsTrigger>
                    ))}
                  </TabsList>

                  {Object.keys(groupedForms).map(cat => (
                    <TabsContent key={cat} value={cat} className="flex-1 overflow-y-auto mt-3 space-y-2 pr-1">
                      {groupedForms[cat].length === 0 ? (
                        <p className="text-[#A1A1AA] text-center py-8 text-sm">No {cat} forms assigned</p>
                      ) : groupedForms[cat].map(sf => {
                        const profKey = norm(sf.proficiency || "Learning");
                        const isUp = updating === `${selected.id}-${sf.form_id}`;
                        return (
                        <div key={sf.id} className="bg-[#18181b] border border-white/10 rounded-sm p-3">
                          <div className="flex items-start gap-3">
                            <div className="min-w-0 flex-1">
                              <p className="text-white font-semibold text-[15px]">
                                {sf.form?.gate_info && <span className="text-teal-400 text-[10px] font-bold px-1.5 py-0.5 bg-teal-500/10 border border-teal-500/20 rounded-sm mr-2">{sf.form.gate_info}</span>}
                                {sf.form?.english_name || 'Unknown'}
                              </p>
                              <p className="text-[#A1A1AA] text-xs mt-0.5">{sf.form?.cantonese || ''} | {sf.form?.traditional_chinese || ''}</p>
                              {sf.form?.skill_focus && <p className="text-teal-300 text-[10px] mt-0.5">{sf.form.skill_focus}{sf.form.min_months ? ` · ${sf.form.min_months}mo min` : ''}</p>}
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <Button size="sm" variant="ghost" onClick={() => openHistory(sf, sf.form)}
                                className="text-[#A1A1AA] hover:text-white p-2" title="Note history">
                                <Clock className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => openNotesDialog(sf, sf.form)}
                                className={`p-2 ${sf.notes ? 'text-teal-400' : 'text-[#A1A1AA]'} hover:text-white`} title="Add note">
                                <FileText className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" data-testid={`tc-delete-form-${sf.id}`}
                                onClick={() => setDeleteConfirm({ id: sf.id, name: sf.form?.english_name || 'this form' })}
                                className="text-[#555] hover:text-red-400 p-2" title="Remove form">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          {/* Color-Coded Proficiency Buttons */}
                          <div data-testid={`tc-proficiency-toggle-${sf.id}`} className="flex gap-1 mt-2">
                            {PROFICIENCY_LEVELS.map((level) => {
                              const active = profKey === level.key;
                              return (
                                <button key={level.key} disabled={isUp}
                                  onClick={() => handleProficiencyChange(sf.id, level.key, selected.id, sf.form_id)}
                                  className={`flex-1 py-2 text-xs font-semibold rounded-sm transition-all ${
                                    active
                                      ? `${level.activeColor} ${level.textColor} ring-2 ring-white/50 shadow-lg`
                                      : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10'
                                  } ${isUp ? 'opacity-50' : ''}`}
                                  title={level.key}>
                                  <span className="hidden sm:inline">{level.key}</span>
                                  <span className="sm:hidden">{level.short}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        );
                      })}
                    </TabsContent>
                  ))}
                </Tabs>
              </>
            )}
          </div>
        </div>
      </div>
      )}

      {/* ========== MASTERY MATRIX MODAL ========== */}
      <Dialog open={showMatrix} onOpenChange={setShowMatrix}>
        <DialogContent className="bg-[#18181b] border-white/10 text-white max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="text-[#D4AF37] flex items-center gap-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
              <LayoutGrid className="w-5 h-5" />Mastery Matrix
            </DialogTitle>
          </DialogHeader>

          <div className="mt-2 space-y-3 flex-1 overflow-hidden flex flex-col">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
              <Input ref={matrixSearchRef} data-testid="matrix-search"
                value={matrixSearch} onChange={(e) => { setMatrixSearch(e.target.value); setMatrixFormId(null); setMatrixAssignments([]); }}
                className="pl-10 bg-white/5 border-white/10 text-white h-10"
                placeholder="Search forms by name..." />
            </div>

            {!matrixFormId ? (
              <div data-testid="matrix-form-list" className="flex-1 overflow-y-auto space-y-1 pr-1">
                {matrixFilteredForms.length === 0 ? (
                  <p className="text-[#A1A1AA] text-center py-8 text-sm">No forms match your search</p>
                ) : matrixFilteredForms.map(f => (
                  <div key={f.id} data-testid={`matrix-form-${f.id}`}
                    onClick={() => handleMatrixFormSelect(f.id)}
                    className="flex items-center gap-3 p-3 bg-[#0f0f10] rounded-sm border border-white/5 hover:border-[#D4AF37]/40 cursor-pointer transition-all hover:bg-white/5">
                    <span className="text-lg flex-shrink-0">
                      {f.category === "Hand Form" ? "✋" : f.category === "Weapons" ? "⚔️" : "🥋"}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium text-sm">{f.english_name}</p>
                      <p className="text-[#A1A1AA] text-xs truncate">{f.cantonese} | {f.traditional_chinese}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto">
                <div className="flex items-center gap-3 mb-3">
                  <Button size="sm" variant="ghost" onClick={() => { setMatrixFormId(null); setMatrixAssignments([]); }} className="text-[#A1A1AA] hover:text-white p-1">
                    <ArrowLeft className="w-4 h-4" />
                  </Button>
                  <div className="min-w-0">
                    <p className="text-white font-semibold">{matrixData?.form?.english_name}</p>
                    <p className="text-[#A1A1AA] text-xs">{matrixData?.form?.cantonese} — {matrixData?.total || 0} students assigned</p>
                  </div>
                </div>

                <div data-testid="matrix-columns" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                  {PROFICIENCY_LEVELS.map(level => {
                    const names = matrixData?.columns?.[level.key] || [];
                    return (
                      <div key={level.key} className="bg-[#0f0f10] rounded-sm border border-white/5 overflow-hidden">
                        <div className={`${level.activeColor} px-2 py-2 text-center`}>
                          <span className={`text-xs font-bold ${level.textColor}`}>{level.key}</span>
                        </div>
                        <div className="p-2 min-h-[60px]">
                          {names.length === 0 ? (
                            <p className="text-[#555] text-xs text-center py-2">—</p>
                          ) : names.map((name, i) => (
                            <p key={i} className="text-white text-xs py-0.5 font-medium">{name}</p>
                          ))}
                        </div>
                        <div className="px-2 py-2 border-t border-white/5 text-center">
                          <span className="text-orange-400 text-base font-bold" style={{ fontFamily: 'Oswald, sans-serif' }}>{names.length}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Form Dialog */}
      <Dialog open={showAddForm} onOpenChange={setShowAddForm}>
        <DialogContent className="bg-[#18181b] border-white/10 text-white max-w-lg max-h-[80vh] overflow-hidden flex flex-col p-0" aria-describedby={undefined}>
          <div className="p-4 pb-0 flex-shrink-0">
            <DialogHeader>
              <DialogTitle className={artStyle === "tai_chi" ? "text-teal-400" : "text-[#D4AF37]"} style={{ fontFamily: 'Oswald, sans-serif' }}>
                Add {artStyle === "tai_chi" ? "Tai Chi" : "Hung Kuen"} Forms to {selected?.name}
                {selectedFormsToAdd.size > 0 && <span className="text-orange-400 text-sm ml-2">({selectedFormsToAdd.size} selected)</span>}
              </DialogTitle>
            </DialogHeader>
            <div className="relative mt-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
              <Input data-testid="add-form-search" value={addFormSearch} onChange={(e) => setAddFormSearch(e.target.value)}
                className="pl-10 bg-white/5 border-white/10 text-white h-9 text-sm" placeholder="Search forms..." />
            </div>
            <div className="flex gap-1.5 mt-2 mb-1">
              {(artStyle === "tai_chi"
                ? [["Qigong/Foundation", "Qigong"], ["Hand Form", "Hand"], ["Weapons", "Weapons"]]
                : [["Hand Form", "Hand"], ["Weapons", "Weapons"], ["Sparring Set", "Sparring"]]
              ).map(([key, label]) => (
                <button key={key} onClick={() => document.getElementById(`addform-cat-${key}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                  className={`flex-1 text-[10px] font-semibold uppercase py-1.5 rounded-sm bg-white/5 text-[#A1A1AA] hover:bg-white/10 hover:text-white transition-colors`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-4 pb-1">
            {(() => {
              const cats = getAvailableForms();
              const catLabels = artStyle === "tai_chi"
                ? { "Qigong/Foundation": "Qigong / Foundation", "Hand Form": "Hand Forms", "Weapons": "Weapons" }
                : { "Hand Form": "Hand Forms", "Weapons": "Weapons", "Sparring Set": "Sparring" };
              const q = addFormSearch.toLowerCase();
              return Object.entries(catLabels).map(([key, label]) => {
                let list = cats[key] || [];
                if (q) list = list.filter(f => f.english_name.toLowerCase().includes(q) || f.cantonese.toLowerCase().includes(q) || (f.gate_info || '').toLowerCase().includes(q));
                if (list.length === 0) return null;
                return (
                  <div key={key} id={`addform-cat-${key}`}>
                    <div className="sticky top-0 z-10 bg-[#18181b] py-2 border-b border-white/10">
                      <p className="text-[#A1A1AA] text-xs uppercase tracking-wider font-bold">{label} <span className="text-orange-400">({list.length})</span></p>
                    </div>
                    <div className="space-y-1 py-1">
                      {list.map(f => {
                        const checked = selectedFormsToAdd.has(f.id);
                        return (
                          <div key={f.id} data-testid={`add-form-${f.id}`}
                            onClick={() => toggleFormSelection(f.id)}
                            className={`flex items-center gap-3 p-2.5 rounded-sm cursor-pointer transition-all border ${
                              checked ? (artStyle === "tai_chi" ? 'bg-teal-600/15 border-teal-500/50' : 'bg-[#D4AF37]/15 border-[#D4AF37]/50') : 'bg-white/3 border-transparent hover:bg-white/5'
                            }`}>
                            <div className={`w-5 h-5 rounded-sm border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                              checked ? (artStyle === "tai_chi" ? 'bg-teal-500 border-teal-500' : 'bg-[#D4AF37] border-[#D4AF37]') : 'border-white/30'
                            }`}>
                              {checked && <span className="text-black text-xs font-bold">✓</span>}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium ${checked ? (artStyle === "tai_chi" ? 'text-teal-400' : 'text-[#D4AF37]') : 'text-white'}`}>
                                {f.gate_info ? `${f.gate_info} — ` : ''}{f.english_name}
                              </p>
                              <p className="text-[#A1A1AA] text-[10px] truncate">
                                {f.cantonese}
                                {f.skill_focus && <span className="ml-2 text-teal-300">{f.skill_focus}</span>}
                                {f.min_months && <span className="ml-1 text-[#A1A1AA]">· {f.min_months}mo</span>}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
          <div className="flex gap-2 p-4 pt-3 border-t border-white/10 flex-shrink-0 bg-[#18181b]">
            <Button onClick={() => setShowAddForm(false)} variant="outline" className="flex-1 border-white/20 text-white hover:bg-white/10">Cancel</Button>
            <Button data-testid="add-forms-submit" onClick={handleAddForm} disabled={selectedFormsToAdd.size === 0}
              className={`flex-1 font-bold ${artStyle === "tai_chi" ? "bg-teal-600 hover:bg-teal-700 text-white" : "bg-[#D4AF37] hover:bg-[#b8962e] text-black"}`}>
              Add {selectedFormsToAdd.size > 0 ? `${selectedFormsToAdd.size} Form${selectedFormsToAdd.size > 1 ? 's' : ''}` : 'Forms'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Notes Dialog */}
      <Dialog open={showNotesDialog} onOpenChange={setShowNotesDialog}>
        <DialogContent className="bg-[#18181b] border-white/10 text-white max-w-lg" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="text-[#D4AF37]" style={{ fontFamily: 'Oswald, sans-serif' }}>Add Note: {editingNote.formName}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-[#A1A1AA] text-xs">New notes are timestamped automatically.</p>
            <Textarea data-testid="new-note-input" value={editingNote.newNote}
              onChange={(e) => setEditingNote(prev => ({ ...prev, newNote: e.target.value }))}
              placeholder="Focus on waist power, tighten stances..." className="bg-white/5 border-white/10 text-white min-h-[80px]" />
            {editingNote.notes && (
              <div className="max-h-32 overflow-y-auto bg-white/5 rounded-sm p-3">
                <p className="text-[#A1A1AA] text-xs uppercase mb-2">Previous notes</p>
                <p className="text-white/70 text-xs whitespace-pre-wrap">{editingNote.notes}</p>
              </div>
            )}
            <div className="flex gap-2">
              <Button onClick={() => setShowNotesDialog(false)} variant="outline" className="flex-1 border-white/20 text-white hover:bg-white/10">Cancel</Button>
              <Button onClick={handleSaveNotes} disabled={savingNote || !editingNote.newNote.trim()} className="flex-1 bg-[#D4AF37] hover:bg-[#b8962e] text-black">
                <Save className="w-4 h-4 mr-2" />{savingNote ? "Saving..." : "Save Note"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Note History Dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="bg-[#18181b] border-white/10 text-white max-w-lg" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="text-[#D4AF37] flex items-center gap-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
              <Clock className="w-5 h-5" />Note History: {historyFormName}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 max-h-80 overflow-y-auto space-y-3">
            {historyEntries.length === 0 ? (
              <p className="text-[#A1A1AA] text-center py-8">No notes recorded yet</p>
            ) : historyEntries.map((entry, i) => (
              <div key={i} className="bg-white/5 rounded-sm p-3 border-l-2 border-[#D4AF37]">
                <p className="text-[#D4AF37] text-xs font-medium mb-1">{entry.date}</p>
                <p className="text-white text-sm">{entry.text}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Rank Deep Dive Dialog */}
      <Dialog open={showRankDive} onOpenChange={setShowRankDive}>
        <DialogContent className="bg-[#18181b] border-white/10 text-white max-w-md" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="text-[#D4AF37] flex items-center gap-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
              <Users className="w-5 h-5" />{rankDiveTitle}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-3">
            <p className="text-orange-400 font-bold text-lg mb-3" style={{ fontFamily: 'Oswald, sans-serif' }}>
              {rankDiveStudents.length} {rankDiveStudents.length === 1 ? 'Student' : 'Students'}
            </p>
            {rankDiveStudents.length === 0 ? (
              <p className="text-[#A1A1AA] text-center py-6">No students at this rank</p>
            ) : (
              <div className="max-h-64 overflow-y-auto space-y-1">
                {rankDiveStudents.map(s => (
                  <div key={s.id} className="flex items-center gap-3 p-2 bg-white/5 rounded-sm">
                    <div className={`w-8 h-8 rounded-sm flex items-center justify-center flex-shrink-0 ${s.is_sifu ? 'bg-gradient-to-br from-[#D4AF37] to-[#b8962e]' : isAdult(s) ? 'bg-[#D92323]' : 'bg-orange-500'}`}>
                      <span className={`font-bold text-xs ${s.is_sifu ? 'text-black' : 'text-white'}`}>{s.name?.charAt(0)}</span>
                    </div>
                    <span className="text-white text-sm font-medium">{s.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Form Confirmation */}
      {deleteConfirm && (
        <div data-testid="delete-form-dialog" className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setDeleteConfirm(null)}>
          <div className="bg-[#18181b] border border-red-500/30 rounded-lg p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <h3 className="text-white font-bold text-lg mb-2" style={{ fontFamily: 'Oswald, sans-serif' }}>Confirm Delete?</h3>
            <p className="text-[#A1A1AA] text-sm mb-4">
              Remove <span className="text-white font-semibold">{deleteConfirm.name}</span> from this student's training log?
            </p>
            <p className="text-[#555] text-xs mb-4">This cannot be undone.</p>
            <div className="flex gap-2">
              <Button data-testid="confirm-delete-form" onClick={() => handleDeleteForm(deleteConfirm.id)}
                className="flex-1 bg-red-600 hover:bg-red-500 text-white font-bold uppercase tracking-wider text-sm">
                Delete
              </Button>
              <Button variant="ghost" onClick={() => setDeleteConfirm(null)} className="flex-1 text-[#A1A1AA]">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
